'use client'

import httpClient from '@apiClient'
import TrackButton, { useTrackButton } from '@component/card/listings/track'
import CheckboxContent from '@component/form/checkbox/content'
import useEnquiry from '@hooks/useEnquiry'
import useFetch from '@hooks/useFetch'
import useLogin from '@hooks/useLogin'
import { enquiryDataLayer } from '@plugin/google/gtm/modules/property'
import { getHouseId, getSoldLabel, isEstimateData, isOfflineData, isPropertyData, isRentData, isSoldData, isVillageData } from '@utils/house'
import { assign, find, first, get } from 'lodash-es'
import { useCallback, useEffect, useState } from 'react'
import { useAsyncFn } from 'react-use'
import FreeAppraisalButton from '../track/freeAppraisal'

export default function NoAvm({
  data
}) {
  const { tracked } = useTrackButton({ data })
  const { isLogin } = useLogin()
  const isEstimate = isEstimateData(data)
  const isOffline = isOfflineData(data)
  const isProperty = isPropertyData(data)
  const hasSoldInfo = Boolean(getSoldLabel(data)) || isSoldData(data)
  const showAppraisalButton = !data.houseId

  const { showHouseEnquiry } = useEnquiry()
  const requestHandle = useCallback(event => {
    enquiryDataLayer.click()
    showHouseEnquiry({
      house: data,
      agent: first(data.agents),
      signupSource: 'agent_enquiry',
      sendAllAgent: true
    })(event)
  }, [showHouseEnquiry, data])

  return (
    <div className='sm:py-18 sm:px-48 space-y-20 text-center md:text-left'>
      {
        hasSoldInfo
          ? (
            <>
              <div className='text-lg text-secondary'>Insufficient data to provide an estimate for this property</div>
              <div className='text-3xl text-font font-bold'>Get notifications when this property gets a new valuation.</div>
            </>
          )
          : isEstimate
            ? (
              <>
                <div className='text-lg text-secondary'>Insufficient data to provide an estimate for this property</div>
                <div className='text-3xl text-font font-bold'>Thinking of selling?</div>
                <div className='text-xl'>Get a free, no obligation appraisal from a suburb expert.</div>
                {
                  showAppraisalButton && (
                    <FreeAppraisalButton data={data} className='button-primary w-full sm:w-170' />
                  )
                }
              </>
            )
            : (
              <>
                <div className='text-lg text-secondary'>Insufficient data to provide an estimate for this property</div>
                <div className='text-3xl text-font font-bold'>Contact the agent today to get a price indication for this property.</div>
                {
                  isLogin
                    ? (
                      <EnquireForm data={data} />
                    )
                    : (
                      <button className='button-primary w-full sm:w-170' onClick={requestHandle}>Enquire now</button>
                    )
                }
              </>
            )
      }
      {
        isProperty && (
          (isOffline && tracked) || (hasSoldInfo)
            ? <TrackButton data={data} signupSource='Track_Home' dark />
            : (
              <div className='text-lg'>
                {
                  tracked
                    ? (
                      <>
                        <TrackButton buttonDisplay={false} content="Stop tracking" data={data} signupSource='Track_Home' className='text-primary underline' /> property and receiving updates on valuation.
                      </>
                    )
                    : (
                      <>
                        OR <TrackButton buttonDisplay={false} content="Click here" data={data} signupSource='Track_Home' className='text-primary underline' /> to track property and get updates on valuation.
                      </>
                    )
                }
              </div>
            )
        )
      }
    </div>
  )
}

function EnquireForm({
  data
}) {
  const { showHouseEnquiry, enquirySuccess } = useEnquiry()

  const { data: user } = useFetch('user/info')

  const [isReceiveBizEmail, setIsReceiveBizEmail] = useState(true)
  const [email, setEmail] = useState('')

  useEffect(() => {
    if (user) {
      setEmail(get(user, 'email', ''))
    }
  }, [user, setEmail])


  const [submitState, submitHandle] = useAsyncFn(async (event) => {
    event.preventDefault()
    let type
    // if (isProjectData(data)) {
    //   type = 'property'
    // } else 
    if (isVillageData(data)) {
      type = 'village'
    } else if (isRentData(data)) {
      type = 'rental'
    } else {
      type = 'house'
    }

    const enquirySpecs = await httpClient.get('enum/enquiry-specs', { type, id: getHouseId(data) })
    const defalutSpecs = find(enquirySpecs, item => item.value === 'default') || first(enquirySpecs)

    const params = assign(
      {
        type,
        name: get(user, 'firstName'),
        email,
        isReceiveBizEmail: isReceiveBizEmail ? 1 : 0,
        id: getHouseId(data),
        propertyAddress: get(data, 'address', ''),
        spec: get(defalutSpecs, 'value'),
        body: get(defalutSpecs, 'default', ''),
      }
    )
    await httpClient.post('user/enquiry', params)
    showHouseEnquiry({ house: data })(event)
    enquirySuccess(params)
  }, [isReceiveBizEmail, email, user, data, showHouseEnquiry, enquirySuccess])

  return (
    <div>
      <form className='flex gap-x-16' onSubmit={submitHandle}>
        <input type='text' className='flex-1 border rounded-sm max-w-340 px-10 h-40' value={email} onChange={e => setEmail(e.target.value)} />
        <button type='submit' className='button-orange hidden md:block'>{submitState.loading ? 'loading...' : 'Enquire now'}</button>
      </form>
      <CheckboxContent checked={isReceiveBizEmail} onChange={setIsReceiveBizEmail} className='mt-10'>
        <span className='text-neutral-5'>I would like to receive emails with property info, updates and more from OneRoof</span>
      </CheckboxContent>
      <button type='submit' className='button-orange w-full mt-10 md:hidden'>{submitState.loading ? 'loading...' : 'Enquire now'}</button>
    </div>
  )
}
